<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddPipeline from './AddPipeline.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedPipeline = ref({});

const records = computed(() => getters['pipelines/getPipelines'].value);
const uiFlags = computed(() => getters['pipelines/getUIFlags'].value);
const defaultPipelineId = computed(
  () => getters['pipelines/getDefaultPipelineId'].value
);

const deleteMessage = computed(() => ` ${selectedPipeline.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPage = pipeline => {
  router.push({
    name: 'pipeline_edit',
    params: { pipelineId: pipeline.id },
  });
};

const openDeletePopup = pipeline => {
  showDeleteConfirmationPopup.value = true;
  selectedPipeline.value = pipeline;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deletePipeline = async id => {
  try {
    await store.dispatch('pipelines/delete', id);
    useAlert(t('PIPELINES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedPipeline.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedPipeline.value.id] = true;
  closeDeletePopup();
  deletePipeline(selectedPipeline.value.id);
};

const setAsDefault = async pipeline => {
  try {
    loading.value[pipeline.id] = true;
    await store.dispatch('pipelines/setDefault', pipeline.id);
    useAlert(t('PIPELINES.SET_DEFAULT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.SET_DEFAULT.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[pipeline.id] = false;
  }
};

const tableHeaders = computed(() => {
  return [
    t('PIPELINES.LIST.TABLE_HEADER.NAME'),
    t('PIPELINES.LIST.TABLE_HEADER.STAGES'),
    t('PIPELINES.LIST.TABLE_HEADER.DEFAULT'),
  ];
});

onBeforeMount(() => {
  store.dispatch('pipelines/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PIPELINES.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('PIPELINES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PIPELINES.HEADER')"
        :description="$t('PIPELINES.DESCRIPTION')"
        feature-name="pipelines"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('PIPELINES.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="pipeline in records" :key="pipeline.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col">
                <span class="mb-1 font-medium break-words text-n-slate-12">
                  {{ pipeline.name }}
                </span>
                <span
                  v-if="pipeline.description"
                  class="text-sm text-n-slate-11"
                >
                  {{ pipeline.description }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="stage in pipeline.stages?.slice(0, 5)"
                  :key="stage.id"
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md bg-n-alpha-2"
                >
                  <span
                    class="w-2 h-2 mr-1 rounded-full"
                    :style="{ backgroundColor: stage.border_color }"
                  />
                  {{ stage.name }}
                </span>
                <span
                  v-if="pipeline.stages?.length > 5"
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md bg-n-alpha-2"
                >
                  {{ `+${pipeline.stages.length - 5}` }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                v-if="defaultPipelineId === pipeline.id"
                class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md bg-woot-100 text-woot-600 dark:bg-woot-800 dark:text-woot-200"
              >
                {{ $t('PIPELINES.DEFAULT_BADGE') }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-if="defaultPipelineId !== pipeline.id"
                  v-tooltip.top="$t('PIPELINES.SET_DEFAULT.TOOLTIP')"
                  icon="i-lucide-star"
                  slate
                  xs
                  faded
                  :is-loading="loading[pipeline.id]"
                  @click="setAsDefault(pipeline)"
                />
                <Button
                  v-tooltip.top="$t('PIPELINES.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[pipeline.id]"
                  @click="openEditPage(pipeline)"
                />
                <Button
                  v-tooltip.top="$t('PIPELINES.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[pipeline.id]"
                  :disabled="records.length <= 1"
                  @click="openDeletePopup(pipeline)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddPipeline @close="hideAddPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('PIPELINES.DELETE.CONFIRM.TITLE')"
      :message="$t('PIPELINES.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('PIPELINES.DELETE.CONFIRM.YES')"
      :reject-text="$t('PIPELINES.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
